import { Field, FormikProps } from 'formik';
import _ from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';

import { Select, Typography, TextField } from '@vivaahealth/design-system';

import styles from './edit-insurance-page.module.css';

import { MEDICARE_PAYORS } from '../types';

import * as api from '../../../../../api';
import { DatePicker } from '../../../../../components/DatePicker';
import LabeledInput from '../../../../../components/LabeledInput';
import * as store from '../../../../../reducers/store';
import { useCallback } from 'react';

import { getPayorDisplayName } from '../utils';

interface GeneralDetailsProps extends WithTranslation {
  formikProps: FormikProps<api.PrevisitPatientInsurance.InsuranceCoverage>;
  patientInsuranceInformationPayors: store.Loaded['masterPracticePreference']['patientInsuranceInformationPayors'];
  payorsDisplayNameMapping: Record<string, string>;
  subscribersById: Record<
    string,
    api.PrevisitPatientInsurance.PrevisitSubscriber
  >;
}

const GeneralDetailsForm = ({
  formikProps,
  patientInsuranceInformationPayors,
  payorsDisplayNameMapping,
  subscribersById,
  t,
}: GeneralDetailsProps) => {
  const payor = formikProps.values
    .payor as api.PatientInsuranceInformation.Payor;

  const getTitle = useCallback((): string => {
    if (
      formikProps.initialValues.payor ===
      api.PatientInsuranceInformation.Payor.Other
    ) {
      return t('otherPredictedTitle');
    }

    return t('ocrIssueTitle');
  }, [formikProps.initialValues.payor, t]);

  return (
    <>
      <Typography variant="headline-small" bold>
        {getTitle()}
      </Typography>

      <Select
        name="payor"
        label={t('insuranceProvider')}
        value={{
          label: Boolean(payor)
            ? t(
                getPayorDisplayName({
                  payor,
                  payorsDisplayNameMapping,
                }),
              )
            : '',
          value: payor || '',
        }}
        error={formikProps.errors.payor}
        onBlur={() => formikProps.setFieldTouched('payor', true, false)}
        onChange={({ value: newPayor }: any) => {
          const payorIsMedicare = MEDICARE_PAYORS.includes(payor);
          const newPayorIsMedicare = MEDICARE_PAYORS.includes(newPayor);
          if (payorIsMedicare && !newPayorIsMedicare) {
            formikProps.setValues({
              ...formikProps.values,
              subscriberId: null,
              payor: newPayor,
            });
            return;
          }
          if (!payorIsMedicare && newPayorIsMedicare) {
            const selfSubscriber = _.find(
              subscribersById,
              (s) =>
                s.patientRelationshipToPrimaryPolicyholder ===
                api.Common.Relationship.Self,
            );
            formikProps.setValues({
              ...formikProps.values,
              ...(!!selfSubscriber && {
                subscriberId: selfSubscriber?.id,
              }),
              payor: newPayor,
            });
            return;
          }

          formikProps.setFieldValue('payor', newPayor);
        }}
        options={patientInsuranceInformationPayors.map(
          (patientInsuranceInformationPayor) => ({
            label: t(
              getPayorDisplayName({
                payor: patientInsuranceInformationPayor,
                payorsDisplayNameMapping,
              }),
            ),
            value: patientInsuranceInformationPayor,
          }),
        )}
      />
      {payor && (
        <>
          <Field
            as={TextField}
            name={'primaryPolicyHolderMemberId'}
            label={t('memberId', { ns: 'insurance' })}
            error={
              formikProps.touched.primaryPolicyHolderMemberId
                ? formikProps.errors.primaryPolicyHolderMemberId
                : formikProps.initialErrors.primaryPolicyHolderMemberId
            }
          />
          {formikProps.values.payor &&
            !MEDICARE_PAYORS.includes(formikProps.values.payor) && (
              <Field
                as={TextField}
                name={'groupNumber'}
                label={t('groupNumber', { ns: 'insurance' })}
              />
            )}
          <div className={styles['labeled-input-wrapper']}>
            <LabeledInput
              label={t('coverageStartDate', { ns: 'insurance' })}
              subLabel={t('fillOutNow')}
              htmlFor="insurance-card-info-coverage-start-date"
            >
              <DatePicker
                curvedInput={5}
                date={
                  formikProps.values.coverageStartDate &&
                  !isNaN(
                    new Date(formikProps.values.coverageStartDate).valueOf(),
                  )
                    ? new Date(
                        formikProps.values.coverageStartDate,
                      ).toISOString()
                    : ''
                }
                onChange={(coverageStartDate) => {
                  formikProps.setFieldValue(
                    'coverageStartDate',
                    coverageStartDate,
                  );
                }}
              />
            </LabeledInput>
          </div>
        </>
      )}
    </>
  );
};

export default withTranslation(['editInsurancePage', 'insurance'])(
  GeneralDetailsForm,
);
